import { useCallback, useRef, useState, useEffect } from 'react';
import { useReactFlow } from '@xyflow/react';

interface UseNodeResizeOptions {
  /** Current persisted width (from node data) */
  width?: number;
  /** Minimum width in pixels */
  minWidth?: number;
  /** Maximum width in pixels */
  maxWidth?: number;
  /** Callback to persist the new width */
  onWidthChange?: (width: number) => void;
}

/**
 * Hook that enables horizontal resize on a node via a right-edge drag handle.
 * Returns the current width and props to spread onto a resize handle div.
 */
export function useNodeResize({
  width: persistedWidth,
  minWidth = 180,
  maxWidth = 600,
  onWidthChange,
}: UseNodeResizeOptions) {
  const [localWidth, setLocalWidth] = useState<number | undefined>(persistedWidth);
  const widthRef = useRef<number | undefined>(persistedWidth);
  const { getZoom } = useReactFlow();

  // Sync when persisted width changes externally
  useEffect(() => {
    if (persistedWidth !== undefined) {
      setLocalWidth(persistedWidth);
      widthRef.current = persistedWidth;
    }
  }, [persistedWidth]);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Capture pointer so all move/up events route here even if finger leaves element
    (e.target as HTMLElement).setPointerCapture(e.pointerId);

    const startX = e.clientX;
    const zoom = getZoom();
    const nodeEl = (e.target as HTMLElement).closest('.react-flow__node');
    // getBoundingClientRect returns screen pixels (scaled by zoom),
    // so divide by zoom to get the actual CSS/flow width
    const startWidth = nodeEl
      ? nodeEl.getBoundingClientRect().width / zoom
      : (widthRef.current || minWidth);

    const onPointerMove = (moveEvt: PointerEvent) => {
      moveEvt.preventDefault();
      // Pointer delta is in screen pixels, divide by zoom to convert to flow coordinates
      const delta = (moveEvt.clientX - startX) / zoom;
      const newWidth = Math.round(Math.max(minWidth, Math.min(maxWidth, startWidth + delta)));
      setLocalWidth(newWidth);
      widthRef.current = newWidth;
    };

    const onPointerUp = (upEvt: PointerEvent) => {
      (upEvt.target as HTMLElement).releasePointerCapture?.(upEvt.pointerId);
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', onPointerUp);
      document.removeEventListener('pointercancel', onPointerUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';

      if (onWidthChange && widthRef.current !== undefined) {
        onWidthChange(widthRef.current);
      }
    };

    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', onPointerUp);
    document.addEventListener('pointercancel', onPointerUp);
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';
  }, [minWidth, maxWidth, onWidthChange, getZoom]);

  return {
    nodeWidth: localWidth,
    resizeHandleProps: {
      onPointerDown,
      className: 'absolute top-0 right-0 w-2 h-full cursor-ew-resize z-10 hover:bg-primary-300/40 active:bg-primary-300/60 transition-colors nodrag nopan',
      style: { borderRadius: '0 8px 8px 0', touchAction: 'none' } as React.CSSProperties,
    },
  };
}
